import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import "../styles/message-dropdown.scss";
import actions from "../actions/actions";
import { GuiButton } from "../components/global-ui/gui-button";

// Constants moved outside component to prevent recreation on each render
const BLANK_ALERT = {
  name: "",
  isScheduled: false,
  subject: "",
  htmlMessage: "",
  allGroups: false,
  groups: [],
  isEmergency: false,
  attachments: [],
};

const MESSAGE_TYPES = {
  text: { alertType: 3, icon: "fa fa-mobile", label: "Text" },
  email: { alertType: 2, icon: "fa fa-envelope", label: "Email" },
  voice: { alertType: 1, icon: "fa fa-microphone", label: "Voice" },
};

const EmbedMessageDropdown = ({ isEditing = false, from }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Memoized toggle handler to prevent unnecessary re-renders
  const handleToggle = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);

  // Memoized outside click handler
  const handleOutsideClick = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  // Optimized effect for event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("click", handleOutsideClick);
      return () => document.removeEventListener("click", handleOutsideClick);
    }
  }, [isOpen, handleOutsideClick]);

  // Memoized select handler to prevent recreation on each render
  const handleSelect = useCallback(
    (type) => {
      setIsOpen(false);

      const messageConfig = MESSAGE_TYPES[type];
      if (messageConfig) {
        actions.embedAlerts.showEditAlert(
          { ...BLANK_ALERT, alertType: messageConfig.alertType },
          from,
        );
      }
    },
    [from],
  );

  // Memoized dropdown items to prevent unnecessary re-renders
  const dropdownItems = useMemo(() => {
    return Object.entries(MESSAGE_TYPES).map(([type, config]) => (
      <div
        key={type}
        className="dropdown-item"
        onClick={() => handleSelect(type)}
        role="menuitem"
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect(type);
          }
        }}
      >
        <i className={config.icon}></i>
        <span>{config.label}</span>
      </div>
    ));
  }, [handleSelect]);

  return (
    <div className="message-dropdown" ref={dropdownRef}>
      <GuiButton
        className="dropdown-button"
        onClick={handleToggle}
        disabled={isEditing}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        New Message <i className="fa fa-chevron-down"></i>
      </GuiButton>
      {isOpen && (
        <div className="dropdown-menu" role="menu">
          {dropdownItems}
        </div>
      )}
    </div>
  );
};

// PropTypes for better performance and debugging
EmbedMessageDropdown.propTypes = {
  isEditing: PropTypes.bool,
  from: PropTypes.any,
};

export default EmbedMessageDropdown;
